import React, { type ReactNode } from 'react';

import { Alert } from 'antd';
import moment, { type Moment } from 'moment';

import { DAYS_OF_WEEK } from '@src/constants/commons';
import ShiftService from '@src/services/ShiftService';
import { translateString } from '@src/util/intl/translateString';

import ActiveDay from '../components/ActiveDay';
import { SHIFT_TYPES, type ShiftType } from '../constants';
import type {
  ActiveDaySerialized,
  ActiveDayValue,
  Recurrence,
  SelectOption,
  ShiftErrorsState,
  ShiftFormValues,
  ShiftHoursResult,
  ShiftWarningsState,
} from '../types';
import styles from './styles.module.less';

type DaySchedule = ActiveDayValue | ActiveDaySerialized;

interface DayGroupItem {
  key: string | number;
  short: string;
  schedule: DaySchedule;
  order?: number;
}

interface DayGroup {
  startDay: DayGroupItem;
  endDay: DayGroupItem;
  schedule: DaySchedule;
}

interface DynamicColumn {
  title: string;
  dataIndex: string | number;
  order: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
  fixed?: 'left' | 'right';
  className?: string;
}

interface SourceWeekRow {
  key: number;
  week: string;
  weekValue: number;
  [key: string]: ReactNode | number | string;
}

interface BuildDynamicSourceParams {
  getFieldValue: (name: string) => unknown;
  onChange: (values: ActiveDayValue, meta: { week: number; day: string | number }) => void;
  errors?: ShiftErrorsState | null;
  warnings?: ShiftWarningsState | null;
  disabled?: boolean;
}

export const buildFilteredDays = (
  existingDays: Record<string | number, DaySchedule>,
  daysSelected: Array<string | number>,
): Record<string | number, DaySchedule> =>
  daysSelected.reduce<Record<string | number, DaySchedule>>((acc, d) => {
    if (existingDays[d]) {
      acc[d] = existingDays[d];
    }
    return acc;
  }, {});

export const DAYS_OF_WEEK_OPTIONS: SelectOption<number>[] = DAYS_OF_WEEK.map(day => ({
  label: day.short,
  value: day.value,
}));

export const buildDynamicColumns = (days: Array<string | number> = []): DynamicColumn[] => {
  const columns: DynamicColumn[] = days.map(day => {
    const dayInfo = DAYS_OF_WEEK.find(d => d.value === day);
    return {
      title: dayInfo.label,
      dataIndex: day,
      order: dayInfo.order,
    };
  });

  // insert first column as week name
  columns.unshift({
    title: translateString('shifts.weeks'),
    dataIndex: 'week',
    order: -1,
    width: 80,
    align: 'center',
    fixed: 'left',
  });

  // Add last column
  columns.push({
    title: 'Subtotal',
    dataIndex: 'subtotal',
    order: 1000,
    width: 110,
    align: 'center',
    className: styles.shitTableSubtotal,
  });

  const outputColumns = [...columns];
  return outputColumns.sort((a, b) => a.order - b.order);
};

// Calculate "totalShiftHours"
const calculateSubtotal = (weekNumber: number, recurrence: Recurrence | null | undefined): string | number => {
  if (!recurrence || !recurrence.weeks || !Array.isArray(recurrence.weeks)) {
    return 0;
  }
  // Find the week data
  const weekData = recurrence.weeks.find(w => w.week === weekNumber);

  if (!weekData || !weekData.days || typeof weekData.days !== 'object') {
    return 0;
  }

  return ShiftService.calculateTotalShiftHoursDynamicDays(weekData).formated;
};

export const buildDynamicSource = ({
  getFieldValue,
  onChange,
  errors,
  warnings,
  disabled = false,
}: BuildDynamicSourceParams): SourceWeekRow[] => {
  const totalWeeks = getFieldValue('weekCount') as number;
  const days = getFieldValue('days') as Array<string | number>;
  const currentRecurrence = getFieldValue('recurrence') as Recurrence | null | undefined;

  // Asign weeks
  let source: SourceWeekRow[] = [...Array(totalWeeks)].map((_, idx) => ({
    key: idx,
    week: translateString(`shifts.week.${idx + 1}`),
    weekValue: idx + 1,
  }));

  // Render Error by Type
  const renderErrorsByType = (
    errorsByType: ShiftErrorsState | Record<string, never> = {},
    week: Partial<SourceWeekRow> = {},
    day: string | number,
    type: string,
  ): ReactNode => {
    if (
      errorsByType &&
      errorsByType.byWeek &&
      week &&
      typeof week.weekValue !== 'undefined' &&
      errorsByType.byWeek[week.weekValue] &&
      errorsByType.byWeek[week.weekValue][day] &&
      errorsByType.byWeek[week.weekValue][day][type] &&
      Array.isArray(errorsByType.byWeek[week.weekValue][day][type])
    ) {
      return errorsByType.byWeek[week.weekValue][day][type].map((e, idx) => (
        <Alert
          key={idx}
          description={e?.message}
          type="error"
          showIcon
          className={styles.overlayAlert}
        />
      ));
    } else {
      return null;
    }
  };

  // Render Warning by Type
  const renderWarningsByType = (
    warningsByType: ShiftWarningsState | null | undefined,
    week: number,
    day: string | number,
    type: string,
  ): ReactNode => {
    // Defensive checks for all nested properties
    if (
      !warningsByType ||
      !warningsByType.byWeek ||
      !warningsByType.byWeek[week] ||
      !warningsByType.byWeek[week][day] ||
      !Array.isArray(warningsByType.byWeek[week][day][type])
    ) {
      return null;
    }

    return warningsByType.byWeek[week][day][type].map((e, idx) => (
      <Alert
        key={idx}
        description={e?.message || ''}
        type="warning"
        showIcon
        className={styles.overlayAlert}
        closable
      />
    ));
  };

  // Asign days
  days.forEach(day => {
    source = source.map(week => {
      // Get the specific day data for this week and day combination
      const weekData = currentRecurrence?.weeks?.find(w => w.week === week.weekValue);
      const dayData = weekData?.days?.[day];

      return {
        ...week,
        [day]: (
          <div>
            <ActiveDay
              disabled={disabled}
              includeActiveDay
              defaultActiveDay={false}
              key={`${week.weekValue}-${day}`}
              value={dayData as ActiveDayValue | undefined}
              onChange={values => onChange(values, { week: week.weekValue, day })}
            />
            {renderErrorsByType(errors || {}, week, day, 'breakTime')}
            {renderWarningsByType(warnings, week.weekValue, day, 'rangeHours')}
          </div>
        ),
        subtotal: `${calculateSubtotal(week.weekValue, currentRecurrence)} ${translateString(
          'general.hours',
        )} `,
      };
    });
  });

  return source;
};

export const buildDynamicFooter = (
  recurrence: Recurrence | Record<string, never> = {},
): Pick<ShiftHoursResult, 'hours' | 'formated'> => {
  const weeks = (recurrence as Recurrence)?.weeks || [];

  const totalMinutes = weeks.reduce((acc, week) => {
    const { minutes } = ShiftService.calculateTotalShiftHoursDynamicDays(week);
    return acc + minutes;
  }, 0);

  return {
    hours: parseFloat((totalMinutes / 60).toFixed(2)),
    formated: ShiftService.formatMinutesToHHMM(totalMinutes),
  };
};

// Format time in HH:mm format using Moment.js
function formatTime(momentObj: Moment): string {
  return momentObj.format('HH:mm');
}

// Format time range "HH:mm to HH:mm"
function formatTimeRange(startMoment: Moment | null | undefined, endMoment: Moment | null | undefined): string {
  if (!startMoment || !endMoment) return '';
  return `${formatTime(startMoment)} ${translateString('general.to')} ${formatTime(endMoment)}`;
}

// Compare if two schedules are equal (start and end) using Moment.js
function schedulesEqual(d1: DaySchedule, d2: DaySchedule): boolean {
  if (!d1.startTime || !d1.endTime || !d2.startTime || !d2.endTime) return false;

  return (d1.startTime as Moment).isSame(d2.startTime as Moment) && (d1.endTime as Moment).isSame(d2.endTime as Moment);
}

// Group consecutive days with the same schedule
function groupDays(daysArr: DayGroupItem[]): DayGroup[] {
  const groups: DayGroup[] = [];

  if (!Array.isArray(daysArr) || daysArr.length === 0) return groups;

  // Sort days by their numeric value (0=Sunday, 1=Monday, ..., 6=Saturday)
  const sortedDays = [...daysArr].sort((a, b) => {
    const aValue = parseInt(String(a.key), 10);
    const bValue = parseInt(String(b.key), 10);
    // If one is Sunday (0) and the other is not, Sunday goes at the end
    if (aValue === 0) return 1;
    if (bValue === 0) return -1;
    return aValue - bValue;
  });

  let currentGroup: DayGroup = {
    startDay: sortedDays[0],
    endDay: sortedDays[0],
    schedule: sortedDays[0].schedule,
  };

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < sortedDays.length; i++) {
    const currentDay = sortedDays[i];
    const prevDayValue = parseInt(String(currentGroup.endDay.key), 10);
    const currentDayValue = parseInt(String(currentDay.key), 10);

    // Check if they are consecutive days, considering the special case of Sunday-Saturday
    let isConsecutive = currentDayValue === prevDayValue + 1;
    // Special case: Sunday (0) is consecutive to Saturday (6) - Sunday goes at the end
    if (prevDayValue === 6 && currentDayValue === 0) {
      isConsecutive = true;
    }

    const hasSameSchedule = schedulesEqual(currentDay.schedule, currentGroup.schedule);

    if (isConsecutive && hasSameSchedule) {
      currentGroup.endDay = currentDay;
    } else {
      groups.push(currentGroup);
      currentGroup = {
        startDay: currentDay,
        endDay: currentDay,
        schedule: currentDay.schedule,
      };
    }
  }

  groups.push(currentGroup);
  return groups;
}

// Build string for each group
function buildGroupString(group: DayGroup): string {
  const startShort = group.startDay.short;
  const endShort = group.endDay.short;
  const timeRange = formatTimeRange(
    group.schedule.startTime as Moment | null,
    group.schedule.endTime as Moment | null,
  );

  if (startShort === endShort) {
    return `${startShort} ${timeRange}`;
  }
  return `${startShort} ${translateString('general.to')} ${endShort} ${timeRange}`;
}

export function formatRegulationName(obj: Recurrence): string {
  // Process each week
  return obj.weeks
    .map(weekObj => {
      const activeDays = DAYS_OF_WEEK.map(day => {
        const d = weekObj.days[day.value];
        if (d && d.isActive) {
          return { key: day.value, short: day.short, schedule: d };
        }
        return null;
      }).filter(Boolean) as DayGroupItem[];

      if (activeDays.length === 0) return null;

      const groups = groupDays(activeDays);
      const parts = groups.map(buildGroupString);

      return `${translateString('general.week.short')} ${weekObj.week} ${parts.join(' - ')}`;
    })
    .filter(Boolean)
    .join(' / ');
}
/**
 * Format regulation name only for days
 * @param {Object} obj - The object to format
 * @returns {string} The formatted regulation name
 * @description This function is used to format the regulation name only for days
 * @example
 * formatRegulationNameOnlyDays({
 *   weeks: [
 *     { days: { 1: { isActive: true, startTime: '08:00', endTime: '17:00' } } }
 *   ]
 * })
 */
export function formatRegulationNameOnlyDays(obj: Recurrence | null | undefined): string {
  if (!obj || !obj.weeks) return '';

  // Process the first week to get the active days
  const firstWeek = obj.weeks[0];

  if (!firstWeek || !firstWeek.days) return '';

  // Map the active days of the first week
  const activeDays = DAYS_OF_WEEK.map(day => {
    const d = firstWeek.days[day.value];
    if (d && d.isActive) {
      return {
        key: day.value,
        order: day.order,
        short: day.short,
        schedule: {
          ...d,
          startTime: d.startTime ? moment(d.startTime, 'HH:mm') : null,
          endTime: d.endTime ? moment(d.endTime, 'HH:mm') : null,
          startBreakTime: d.startBreakTime ? moment(d.startBreakTime, 'HH:mm') : null,
        },
      };
    }
    return null;
  }).filter(Boolean) as DayGroupItem[];

  if (activeDays.length === 0) return '';

  // Sort days according to the 'order' field of DAYS_OF_WEEK
  activeDays.sort((a, b) => {
    // Preserve original comparison (string === number) — intentionally loose legacy
    const dayA = DAYS_OF_WEEK.find(day => day.value.toString() === (a.key as unknown as string));
    const dayB = DAYS_OF_WEEK.find(day => day.value.toString() === (b.key as unknown as string));
    return Number(dayA?.order) - Number(dayB?.order);
  });

  // Group consecutive days with the same schedule
  const groups = groupDays(activeDays);
  return groups.map(buildGroupString).join(' - ');
}

/**
 * Builds the full recurrence object for a shift
 * @param {Array} days - Array of selected days
 * @param {Object} recurrence - Recurrence object containing startTime, endTime, and breakTime OR object with day-specific configurations
 * @returns {Object} - Full recurrence object with weeks and totalShiftHours
 * @description
 * This function is used to build the full recurrence object for a shift.
 * It supports two formats and produces the same output structure for both:
 * 1. Legacy format: Same configuration for all days
 * 2. isListOfDays: true -> Day-specific configurations where keys are day numbers
 * @example
 * // Legacy format
 * buildFullRecurrence([1, 2, 3], { startTime: '08:00', endTime: '17:00', breakTime: 30 })
 * // isListOfDays: true
 * buildFullRecurrence([1, 2], { "1": { startTime: '08:00', endTime: '13:00' }, "2": { startTime: '09:00', endTime: '14:00' } })
 */
export const buildFullRecurrence = (
  days: Array<string | number>,
  recurrence: ActiveDayValue | Recurrence | Record<string, DaySchedule> | null | undefined,
  shiftType: ShiftType | string | null = null,
): Recurrence => {
  try {
    const recurrenceData: Recurrence = { weeks: [], totalShiftHours: 0 };

    // Detect if recurrence uses the new format (day-specific configurations)
    const recurrenceRecord = recurrence as Record<string, unknown> | null | undefined;
    const isListOfDays =
      recurrenceRecord &&
      typeof recurrenceRecord === 'object' &&
      !recurrenceRecord.hasOwnProperty('isActive') &&
      !recurrenceRecord.hasOwnProperty('startTime') &&
      !recurrenceRecord.hasOwnProperty('endTime') &&
      Object.keys(recurrenceRecord).some(key => !isNaN(key as unknown as number));

    let daysProcessed: Record<string | number, DaySchedule>;
    let totalMinutes = 0;

    if (isListOfDays) {
      const listRecurrence = recurrence as Record<string, DaySchedule>;
      // isListOfDays: true -> recurrence has day-specific configurations
      daysProcessed = days.reduce<Record<string | number, DaySchedule>>((acc, day) => {
        const dayConfig = listRecurrence[day.toString()];
        if (dayConfig) {
          acc[day] = {
            ...dayConfig,
            startTime: dayConfig.startTime ? moment(dayConfig.startTime).format('HH:mm') : null,
            endTime: dayConfig.endTime ? moment(dayConfig.endTime).format('HH:mm') : null,
            startBreakTime: dayConfig.startBreakTime
              ? moment(dayConfig.startBreakTime).format('HH:mm')
              : null,
          } as ActiveDaySerialized;
        }
        return acc;
      }, {});

      // Calculate total minutes for new format (sum all days)
      totalMinutes = days.reduce<number>((acc, day) => {
        const dayConfig = listRecurrence[day.toString()];
        if (dayConfig && dayConfig.isActive && dayConfig.startTime && dayConfig.endTime) {
          const start = moment(dayConfig.startTime);
          const end = moment(dayConfig.endTime);
          const breakTime = dayConfig.breakTime || 0;
          const dayMinutes = end.diff(start, 'minutes') - breakTime;
          return acc + (dayMinutes > 0 ? dayMinutes : 0);
        }
        return acc;
      }, 0);
    } else {
      const sharedRecurrence = recurrence as ActiveDayValue | null | undefined;
      // isListOfDays: false -> same configuration for all days
      daysProcessed = days.reduce<Record<string | number, DaySchedule>>((acc, day) => {
        acc[day] = {
          ...sharedRecurrence,
          startTime: sharedRecurrence?.startTime
            ? moment(sharedRecurrence?.startTime).format('HH:mm')
            : null,
          endTime: sharedRecurrence?.endTime
            ? moment(sharedRecurrence?.endTime).format('HH:mm')
            : null,
          startBreakTime: sharedRecurrence?.startBreakTime
            ? moment(sharedRecurrence?.startBreakTime).format('HH:mm')
            : null,
        } as ActiveDaySerialized;
        return acc;
      }, {});

      const { minutes } = ShiftService.calculateTotalShiftHours(days, recurrence);
      totalMinutes = minutes;
    }

    const weeklyMinutes = totalMinutes;

    const reportsWithOneWeek: string[] = [SHIFT_TYPES.FIXED, SHIFT_TYPES.PART_TIME];
    const weeksAccordingToType =
      shiftType && reportsWithOneWeek.includes(shiftType)
        ? ShiftService.WEEK_COUNT_CUSTOM
        : ShiftService.WEEK_COUNT;
    recurrenceData.weeks = Array.from({ length: weeksAccordingToType }, (_, i) => ({
      week: i + 1,
      days: daysProcessed,
      totalHours: weeklyMinutes,
    }));

    recurrenceData.totalShiftHours = weeklyMinutes * weeksAccordingToType;
    return recurrenceData;
  } catch (error) {
    throw new Error(`Error building full recurrence: ${(error as Error).message}`);
  }
};

/**
 * Builds regulation name based on form data
 * @param {Object} allFields - All form fields
 * @param {Function} setFieldValue - Function to set form field value
 * @description
 * This function builds the regulation name based on the form data and sets it in the form.
 * It uses buildFullRecurrence and formatRegulationNameOnlyDays to generate the regulation name.
 * @example
 * buildRegulationName(allFields, form.setFieldValue)
 */
export const buildRegulationName = (
  allFields: ShiftFormValues,
  setFieldValue: (name: string, value: unknown) => void,
): void => {
  const preparedData = {
    ...allFields,
    recurrence: buildFullRecurrence(allFields.days, allFields.recurrence as ActiveDayValue | Recurrence | Record<string, DaySchedule>, allFields.type),
  };

  const regulationName = formatRegulationNameOnlyDays(preparedData.recurrence);
  setFieldValue('regulationName', regulationName);
};
